import { useState, useRef, useEffect, useId } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Upload, X, Trash2, Image as ImageIcon } from 'lucide-react';
import type { PhotoResponse } from '@/types/photos';

interface PhotoManagerProps {
  currentPhotos: PhotoResponse[];
  maxPhotos?: number;
  maxFileSizeMB?: number;
  onChange?: (photos: PhotoResponse[], newFiles: File[], deletedPhotoIds: string[]) => void;
  className?: string;
  disabled?: boolean;
  /**
   * How thumbnails fill their box. `cover` (default) crops photos; `contain` shows
   * the whole image on a neutral checkerboard — for logos, so a white mark on a
   * transparent background stays visible.
   */
  previewFit?: 'cover' | 'contain';
  /** Visible label; also names the group and the file input. */
  label?: string;
  /** Help text under the label (announced as the group's description). */
  helpText?: string;
}

/**
 * PhotoManager component for managing photos without immediate API calls.
 * Tracks current photos, new files to upload, and photos to delete.
 * Changes are only applied when the parent form is submitted.
 */
export function PhotoManager({
  currentPhotos = [],
  maxPhotos = 1,
  maxFileSizeMB = 10,
  onChange,
  className,
  disabled = false,
  previewFit = 'cover',
  label,
  helpText
}: PhotoManagerProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const helpId = `${id}-help`;
  const contain = previewFit === 'contain';
  const thumbClass = cn('w-full h-32 rounded-lg', contain ? 'object-contain p-2' : 'object-cover');
  const thumbBackground = contain ? 'bg-checkerboard rounded-lg' : undefined;
  const [isDragging, setIsDragging] = useState(false);
  const [photos, setPhotos] = useState<PhotoResponse[]>(currentPhotos);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update photos when currentPhotos prop changes
  useEffect(() => {
    setPhotos(currentPhotos.filter(p => !deletedPhotoIds.includes(p.id)));
  }, [currentPhotos, deletedPhotoIds]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(photos, newFiles, deletedPhotoIds);
  }, [photos, newFiles, deletedPhotoIds, onChange]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('image/')
    );

    handleFiles(files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
  };

  const handleFiles = (files: File[]) => {
    const maxSizeBytes = maxFileSizeMB * 1024 * 1024;
    const validFiles = files.filter(file => {
      if (file.size > maxSizeBytes) {
        console.warn(`File ${file.name} exceeds ${maxFileSizeMB}MB limit`);
        return false;
      }
      return true;
    });

    // Check if adding these files would exceed maxPhotos
    const totalPhotos = photos.length + newFiles.length + validFiles.length;
    if (totalPhotos > maxPhotos) {
      const allowedCount = maxPhotos - photos.length - newFiles.length;
      if (allowedCount > 0) {
        setNewFiles(prev => [...prev, ...validFiles.slice(0, allowedCount)]);
      }
      console.warn(`Only ${maxPhotos} photo(s) allowed. Some files were not added.`);
    } else {
      setNewFiles(prev => [...prev, ...validFiles]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteExistingPhoto = (photoId: string) => {
    setDeletedPhotoIds(prev => [...prev, photoId]);
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleRestorePhoto = (photoId: string) => {
    setDeletedPhotoIds(prev => prev.filter(id => id !== photoId));
    const restoredPhoto = currentPhotos.find(p => p.id === photoId);
    if (restoredPhoto) {
      setPhotos(prev => [...prev, restoredPhoto]);
    }
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const hasExistingPhotos = photos.length > 0;
  const hasNewFiles = newFiles.length > 0;
  const hasDeletedPhotos = deletedPhotoIds.length > 0;
  const canAddMore = photos.length + newFiles.length < maxPhotos;

  return (
    <div
      className={cn('space-y-4', className)}
      role={label ? 'group' : undefined}
      aria-labelledby={label ? labelId : undefined}
      aria-describedby={label && helpText ? helpId : undefined}
    >
      {(label || helpText) && (
        <div className="space-y-1">
          {label && (
            <p id={labelId} className="text-sm font-medium">
              {label}
            </p>
          )}
          {helpText && (
            <p id={helpId} className="text-xs text-muted-foreground">
              {helpText}
            </p>
          )}
        </div>
      )}
      {/* Display current photos */}
      {hasExistingPhotos && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Current Photo{photos.length > 1 ? 's' : ''}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map((photo) => (
              <Card key={photo.id} className="relative group">
                <div className={thumbBackground}>
                  <img
                    src={photo.url}
                    alt={photo.altText || 'Photo'}
                    className={thumbClass}
                  />
                </div>
                {!disabled && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteExistingPhoto(photo.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Display deleted photos (can be restored) */}
      {hasDeletedPhotos && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Photos to Delete</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {deletedPhotoIds.map((photoId) => {
              const photo = currentPhotos.find(p => p.id === photoId);
              if (!photo) return null;
              return (
                <Card key={photo.id} className="relative group opacity-50">
                  <div className={thumbBackground}>
                    <img
                      src={photo.url}
                      alt={photo.altText || 'Photo'}
                      className={thumbClass}
                    />
                  </div>
                  <div className="absolute inset-0 bg-destructive/20 rounded-lg" />
                  {!disabled && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => handleRestorePhoto(photo.id)}
                    >
                      Restore
                    </Button>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Display new files to upload */}
      {hasNewFiles && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-green-600">New Photos to Upload</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {newFiles.map((file, index) => (
              <Card key={`${file.name}-${index}`} className="relative group">
                <div className={thumbBackground}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className={thumbClass}
                  />
                </div>
                <div className="absolute inset-0 bg-green-500/10 rounded-lg pointer-events-none" />
                {!disabled && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveNewFile(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
                <div className="absolute bottom-2 left-2 text-xs bg-black/50 text-white px-1 rounded">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Upload area - only show if can add more */}
      {canAddMore && (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
            isDragging && !disabled ? 'border-primary bg-primary/10' : 'border-border',
            disabled && 'opacity-50 cursor-not-allowed',
            !disabled && 'cursor-pointer hover:border-primary/50'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm font-medium mb-1">
            {hasExistingPhotos || hasNewFiles
              ? `Add more photos (${photos.length + newFiles.length}/${maxPhotos})`
              : 'Drag and drop photos here, or click to browse'}
          </p>
          <p className="text-xs text-muted-foreground">
            Max {maxFileSizeMB}MB per photo
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple={maxPhotos > 1}
            aria-label={label}
            className="hidden"
            onChange={handleFileInput}
            disabled={disabled}
          />
        </div>
      )}

      {/* Info message when at max capacity */}
      {!canAddMore && (
        <div className="text-sm text-muted-foreground text-center p-4 bg-muted rounded-lg">
          <ImageIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p>Maximum of {maxPhotos} photo{maxPhotos > 1 ? 's' : ''} allowed.</p>
          {!disabled && (
            <p className="text-xs mt-1">Delete existing photos to upload new ones.</p>
          )}
        </div>
      )}

      {/* Summary of changes */}
      {(hasNewFiles || hasDeletedPhotos) && (
        <Card className="p-3 bg-muted/50">
          <p className="text-xs font-medium text-muted-foreground mb-1">Pending Changes:</p>
          <ul className="text-xs space-y-1">
            {hasNewFiles && (
              <li className="text-green-600">• {newFiles.length} photo{newFiles.length > 1 ? 's' : ''} to upload</li>
            )}
            {hasDeletedPhotos && (
              <li className="text-destructive">• {deletedPhotoIds.length} photo{deletedPhotoIds.length > 1 ? 's' : ''} to delete</li>
            )}
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            These changes will be applied when you save the form.
          </p>
        </Card>
      )}
    </div>
  );
}